import React from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { getVideoDimensions } from '../../../../utils/mediaDetector';

const getRadioSeed = (article) => {
  const source = `${article?.title || ''}${article?.siteName || ''}${article?._id || ''}`;
  return source.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
};

const ArticleModalRadioWaveform = ({ article }) => {
  const audioSource = article.audioUrl || article.videoUrl;
  const seed = getRadioSeed(article);
  const barCount = 96;
  const bars = Array.from({ length: barCount }, (_, index) => {
    const wave = Math.sin((index + seed) * 0.58) + Math.sin((index + seed) * 0.19);
    const normalized = (wave + 2) / 4;
    const accentLift = index % 9 === 0 ? 10 : 0;
    return Math.round(16 + normalized * 62 + accentLift);
  });

  return (
    <Box
      sx={{
        mb: 2,
        width: '100%',
        borderRadius: '8px',
        overflow: 'hidden',
        position: 'relative',
        minHeight: { xs: 260, sm: 380, md: 560 },
        background: 'linear-gradient(135deg, #15191c 0%, #1e2b2f 54%, #102724 100%)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(circle at 18% 18%, rgba(34, 197, 94, 0.12), transparent 34%), radial-gradient(circle at 84% 72%, rgba(14, 165, 233, 0.1), transparent 36%)',
          pointerEvents: 'none'
        }
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          inset: { xs: '62px 28px 92px', sm: '86px 48px 108px' },
          display: 'flex',
          alignItems: 'center',
          gap: { xs: '3px', sm: '4px' },
          opacity: 0.96
        }}
      >
        {bars.map((height, index) => (
          <Box
            key={`${height}-${index}`}
            sx={{
              flex: '1 1 0',
              minWidth: 2,
              maxWidth: 8,
              height: `${height}%`,
              borderRadius: '999px',
              backgroundColor: index < Math.round(barCount * 0.44)
                ? 'rgba(45, 212, 191, 0.96)'
                : 'rgba(226, 232, 240, 0.46)',
              boxShadow: index < Math.round(barCount * 0.44)
                ? '0 0 14px rgba(45, 212, 191, 0.24)'
                : 'none',
              transformOrigin: 'center',
              animation: `modalRadioWavePulse 2.8s ease-in-out ${index * 0.025}s infinite`
            }}
          />
        ))}
      </Box>

      {audioSource && (
        <Box
          sx={{
            position: 'absolute',
            left: { xs: 18, sm: 28 },
            right: { xs: 18, sm: 28 },
            bottom: { xs: 18, sm: 24 },
            zIndex: 1,
            '& audio': {
              width: '100%',
              display: 'block'
            }
          }}
        >
          <audio controls preload="metadata" src={audioSource}>
            Seu navegador nao suporta audio.
          </audio>
        </Box>
      )}

      <Box
        component="style"
        dangerouslySetInnerHTML={{
          __html: `
            @keyframes modalRadioWavePulse {
              0%, 100% { transform: scaleY(0.92); opacity: 0.88; }
              50% { transform: scaleY(1); opacity: 1; }
            }
          `
        }}
      />
    </Box>
  );
};

const ArticleModalHostedVideo = ({ article, videoRef, youtubeId }) => {
  if (article.mediaType === 'LinkedIn') return null;

  if (article.mediaType === 'Radio' || article.mediaType === 'Rádio') {
    return <ArticleModalRadioWaveform article={article} />;
  }

  const isStory = article.contentType === 'story';
  const isCloudinaryVideo = article.videoStorageMethod === 'cloudinary';
  const shouldShowVideo = article.videoUrl && !youtubeId &&
    article.mediaType !== 'Twitter' &&
    !(article.mediaType === 'Instagram' && article.embedCode) &&
    !(article.mediaType === 'Instagram' && article.videoStorageMethod === 'embed') &&
    (
      isStory ||
      isCloudinaryVideo ||
      (
        article.videoUrl &&
        !(article.mediaType === 'TikTok' && article.embedCode) &&
        !(article.mediaType === 'Facebook' && article.videoStorageMethod === 'embed')
      )
    );

  if (!shouldShowVideo) return null;

  const videoDimensions = getVideoDimensions(article);
  const isSocialFlexible = ['Pinterest', 'Facebook', 'Instagram', 'TikTok'].includes(article.mediaType);
  const isFacebookVideo = article.mediaType === 'Facebook';
  const videoRadius = article.mediaType === 'Pinterest' ? 24 : 8;

  return (
    <Box
      key={`video-${article._id}-${article.videoUrl}`}
      sx={{
        mb: 2,
        textAlign: 'center',
        width: '100%',
        ...(isSocialFlexible && {
          display: 'flex',
          justifyContent: 'center'
        })
      }}
    >
      <Box
        sx={{
          borderRadius: `${videoRadius}px`,
          overflow: 'hidden',
          display: 'block',
          width: '100%',
          margin: '0 auto',
          maxWidth: videoDimensions.maxWidth || '100%',
          ...(isSocialFlexible && {
            boxShadow: '0 8px 24px rgba(0,0,0,0.2)'
          })
        }}
      >
        <video
          key={`video-element-${article._id}`}
          ref={videoRef}
          src={article.videoUrl}
          poster={article.thumbnail || article.image}
          muted
          loop
          controls
          style={{
            borderRadius: videoRadius,
            width: '100%',
            maxWidth: '100%',
            height: 'auto',
            aspectRatio: videoDimensions.aspectRatio || 'auto',
            maxHeight: isSocialFlexible ? '80vh' : '70vh',
            margin: '0 auto',
            objectFit: isFacebookVideo ? 'contain' : (isSocialFlexible ? 'cover' : 'contain'),
            display: 'block'
          }}
        />
      </Box>
    </Box>
  );
};

ArticleModalHostedVideo.propTypes = {
  article: PropTypes.shape({
    _id: PropTypes.string,
    audioUrl: PropTypes.string,
    contentType: PropTypes.string,
    embedCode: PropTypes.string,
    image: PropTypes.string,
    mediaType: PropTypes.string,
    thumbnail: PropTypes.string,
    videoStorageMethod: PropTypes.string,
    videoUrl: PropTypes.string
  }).isRequired,
  videoRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any })
  ]).isRequired,
  youtubeId: PropTypes.string
};

ArticleModalRadioWaveform.propTypes = {
  article: PropTypes.shape({
    _id: PropTypes.string,
    audioUrl: PropTypes.string,
    siteName: PropTypes.string,
    title: PropTypes.string,
    videoUrl: PropTypes.string
  }).isRequired
};

ArticleModalHostedVideo.defaultProps = {
  youtubeId: null
};

export default ArticleModalHostedVideo;
